
import { useTranslation } from 'react-i18next';

import { useEffect } from 'react';
import UnsavedChangesPrompt from '../unsaved-changes-prompt/UnsavedChangesPrompt';
import { useConfirmation } from '../../contexts/confirmation-context/ConfirmationContext';
import { RenameConfigurationFormValuesType, RenameConfigurationInitType, RenameConfigurationResultType } from './Models';
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useForm } from 'react-hook-form';
import ResponsiveDialog from '../responsive-dialog/ResponsiveDialog';
import Form from '../forms/form/Form';
import FormInput from '../forms/form-input/FormInput';

export type RenameConfigurationDialogResult = DialogResult<RenameConfigurationResultType>;
export type RenameConfigurationDialogProps = DialogProps<RenameConfigurationInitType, RenameConfigurationDialogResult>;

export default function RenameConfigurationDialog({
    open, 
    onClose,
    values,

}: RenameConfigurationDialogProps) {
    
    const { t } = useTranslation();
    const { confirm
    //    , acknowledge 
    } = useConfirmation();
    

    const form = useForm<RenameConfigurationFormValuesType>({
        mode: "onChange",
        defaultValues: {
            newName: values?.configurationName
        } as RenameConfigurationFormValuesType
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid,
        },
        reset,
        //resetField,
        //watch
    } = form;


    async function onSubmit(data: RenameConfigurationFormValuesType) {

        const dialogValues: RenameConfigurationResultType = {...values as RenameConfigurationInitType,
            configurationName: data.newName.trim()
        };

        reset();

        onClose && onClose({
            reason: "ok",
            values: dialogValues
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText:  t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    useEffect(() => {

        reset({
            newName: values?.configurationName
        });
    }, [
        reset, 
        values
    ]);

    return (
        <>
            <ResponsiveDialog
                title={t("configuration.renameDialog.title")}
                enableOkay={isDirty && isValid}
                okayLabel={t("configuration.renameDialog.buttonLabel")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onSubmit(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>
                    <FormInput 
                        id="newName"
                        name='newName'
                        helperText={t("configuration.renameDialog.instructions", {
                            configurationType: values?.configurationType ?? ""
                        })}
                        ariaLabel={t("configuration.renameDialog.inputLabel", {
                            configurationType: values?.configurationType ?? ""
                        })}
                        label={t("configuration.renameDialog.inputLabel", {
                            configurationType: values?.configurationType ?? ""
                        })}
                        rules={{
                            required: {
                                value: true,
                                message: t('configuration.renameDialog.nameRequired')
                            },
                            maxLength: { 
                                value: values?.maxLength ?? 64,
                                message: t('configuration.renameDialog.maxNameLength')
                            },
                            validate: (value: string) => {

                                if (values?.validate) {
                                    return values.validate(value);
                                }

                                return value.trim().length > 0 || t('configuration.nameRequired');
                            } 
                        }}
                        multiline={values?.rows !== undefined}
                        rows={values?.rows}
                    />
                </Form>

            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    );
}

export type RenameConfigurationDialogState = DialogState<RenameConfigurationInitType, RenameConfigurationDialogResult>;
 export type BindRenameConfigurationDialogReturn = BindDialogReturn<RenameConfigurationInitType, RenameConfigurationDialogResult>;

export function useRenameConfigurationDialogState(): RenameConfigurationDialogState {
    return useDialogState();
}

export function bindRenameConfigurationDialog(props: RenameConfigurationDialogState): BindRenameConfigurationDialogReturn {
    return bindDialog(props);
}

